import styled from '@emotion/styled';
import {
    TablePaginationProps as MuiTablePaginationProps,
    Pagination,
    PaginationItem,
    Stack
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DropDownChoices, Dropdown, DropdownType } from './Dropdown';
import React, { useEffect, useState, useRef, useCallback } from 'react';

const StyledPageItem = styled(PaginationItem)(({ theme }) => {
    return {
        '&': {
            display: 'block',
            height: '40px',
            minWidth: '40px',
            textAlign: 'center',
            margin: '0px',
            borderRadius: '0px',
            padding: '10px 10px 10px 10px',
            textDecoration: 'none',
            border: '1px solid #D0D5DD',
            color: theme.palette.text.primary
        },
        '&.Mui-selected': {
            backgroundColor: '#EFF8FF',
            color: theme.palette.text.primary
        },
        '&.MuiPaginationItem-previousNext[aria-label="Go to previous page"]': {
            borderRadius: '8px 0 0 8px'
        },
        '&.MuiPaginationItem-previousNext[aria-label="Go to next page"]': {
            borderRadius: '0 8px 8px 0'
        }
    };
});

type TablePaginationProps<T> = {
    rowsPerPageOptions?: DropDownChoices<T>[];
    onRowsPerPageChange?: (rowsPerPage: T | T[] | null) => void | undefined;
    resetPage?: () => void;
    actions?: React.ReactNode;
    memoryMode?: boolean;
    onPageChange?: (page: number) => void;
} & MuiTablePaginationProps;

export const TablePagination = <T,>({
    count,
    page: externalPage,
    rowsPerPage,
    rowsPerPageOptions,
    resetPage,
    onRowsPerPageChange,
    actions,
    memoryMode = false,
    onPageChange
}: TablePaginationProps<T>) => {
    let pageCount = Math.ceil(count / rowsPerPage);
    const navigate = useNavigate();
    const { pageNumber } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const currentPage = memoryMode ? externalPage : +pageNumber!;

    // Initialize rowsPerPage from query params on mount
    useEffect(() => {
        const rowsParam = searchParams.get('rows');
        if (rowsParam && onRowsPerPageChange) {
            const rowsValue = parseInt(rowsParam);
            if (!isNaN(rowsValue) && rowsValue !== rowsPerPage) {
                const matchingOption = rowsPerPageOptions?.find(
                    (opt) => opt.value === rowsValue
                );
                if (matchingOption) {
                    const timeoutId = setTimeout(() => {
                        onRowsPerPageChange(matchingOption.value);
                    }, 100);
                    return () => clearTimeout(timeoutId);
                }
            }
        }
    }, []);

    // const location = useLocation();

    // const locationWithoutPage = useMemo(() => {
    //     return location.pathname.substring(
    //         0,
    //         location.pathname.lastIndexOf('/page/')
    //     ); // Will break in nested cases , will handle later
    // }, [location]); // calculate only once

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    height: '68px',
                    alignItems: 'center',
                    marginRight: '24px',
                    marginLeft: '24px',
                    justifyContent: 'space-between',
                    boxShadow: '0px 1px 2px 0px #1018280D'
                }}
            >
                <div
                    style={{
                        width: '190px'
                    }}
                >
                    <Dropdown
                        type={DropdownType.SingleShowTitle}
                        direction="up"
                        label="Rows per page:"
                        choices={rowsPerPageOptions}
                        value={rowsPerPage}
                        onChange={(value) => {
                            resetPage?.();
                            onRowsPerPageChange?.(value);
                            // Save to query params
                            const newSearchParams = new URLSearchParams(
                                searchParams
                            );
                            newSearchParams.set('rows', String(value));
                            setSearchParams(newSearchParams, { replace: true });
                        }}
                    />
                </div>
                <Stack direction="row">
                    {pageCount > 1 && (
                        <>
                            <Pagination
                                onChange={(event, page) => {
                                    if (memoryMode) {
                                        onPageChange?.(page - 1);
                                    } else {
                                        const searchString =
                                            searchParams.toString();
                                        const search = searchString
                                            ? `?${searchString}`
                                            : '';
                                        if (page == -1) {
                                            navigate(
                                                `../${
                                                    +pageNumber! - 1
                                                }${search}`,
                                                {
                                                    replace: true,
                                                    relative: 'path'
                                                }
                                            );
                                        } else {
                                            navigate(
                                                `../${page - 1}${search}`,
                                                {
                                                    replace: true,
                                                    relative: 'path'
                                                }
                                            );
                                        }
                                    }
                                }}
                                count={pageCount}
                                variant="outlined"
                                page={currentPage + 1}
                                renderItem={(item) => (
                                    <StyledPageItem {...item} />
                                )}
                            />
                        </>
                    )}
                    {actions}
                </Stack>
            </div>
            <div></div>
        </>
    );
};

export const DEFAULT_ROWS_PER_PAGE = 16;

export const usePagination = (defaultRows: number = DEFAULT_ROWS_PER_PAGE) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { pageNumber } = useParams();

    const [page, setPage] = useState<number>(() => {
        if (pageNumber !== undefined) {
            const parsed = parseInt(pageNumber);
            return !isNaN(parsed) ? parsed : 0;
        }
        return 0;
    });

    // Initialize from query params or use default
    const rowsParam = searchParams.get('rows');
    const initialRows = rowsParam ? parseInt(rowsParam) : defaultRows;
    const [rowsPerPage, setRowsPerPage] = useState<number>(
        !isNaN(initialRows) ? initialRows : defaultRows
    );

    useEffect(() => {
        if (pageNumber) {
            setPage(parseInt(pageNumber));
        }
    }, [pageNumber]);

    // Sync rowsPerPage with query params
    useEffect(() => {
        const rowsParam = searchParams.get('rows');
        if (rowsParam) {
            const rows = parseInt(rowsParam);
            if (!isNaN(rows) && rows !== rowsPerPage) {
                const timeoutId = setTimeout(() => {
                    setRowsPerPage(rows);
                }, 100);
                return () => clearTimeout(timeoutId);
            }
        }
    }, [searchParams]);

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    const resetPage = useCallback(() => {
        // Clear any pending reset
        if (resetTimeoutRef.current) {
            clearTimeout(resetTimeoutRef.current);
        }

        // Use requestAnimationFrame to ensure DOM updates have completed
        // This ensures searchParams are fully updated before we read them
        requestAnimationFrame(() => {
            // Read searchParams fresh from the hook, not from closure
            const currentParams = new URLSearchParams(window.location.search);
            const search = currentParams.toString()
                ? `?${currentParams.toString()}`
                : '';
            navigate(`../0${search}`, { replace: true, relative: 'path' });
        });
    }, [navigate]);

    return {
        page,
        setPage,
        resetPage,
        setRowsPerPage,
        rowsPerPage
    };
};
